import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FiSave, FiCheck, FiAlertCircle, FiPlus, FiX, FiLock, FiEye, FiEyeOff, FiMail, FiServer, FiBarChart2, FiSend } from 'react-icons/fi';
import { getSettings, updateSettings, changePassword, sendTestAnalyticsReport } from '../../services/api';

function SettingsPanel() {
  const [settings, setSettings] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);
  const [newEmail, setNewEmail] = useState('');
  
  // Password change state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordStatus, setPasswordStatus] = useState(null);
  const [passwordError, setPasswordError] = useState('');
  
  // SMTP Settings state
  const [showSmtpPassword, setShowSmtpPassword] = useState(false);
  
  // Analytics Report state
  const [isSendingTestReport, setIsSendingTestReport] = useState(false);
  const [testReportStatus, setTestReportStatus] = useState(null);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setIsLoading(true);
    try {
      const response = await getSettings();
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to load settings:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    setSaveStatus(null);

    try {
      await updateSettings(settings);
      setSaveStatus('success');
      setTimeout(() => setSaveStatus(null), 3000);
    } catch (error) {
      console.error('Save error:', error);
      setSaveStatus('error');
    } finally {
      setIsSaving(false);
    }
  };

  const addEmail = async () => {
    if (newEmail && !settings.notification_emails?.includes(newEmail)) {
      const updatedEmails = [...(settings.notification_emails || []), newEmail];
      const updatedSettings = {
        ...settings,
        notification_emails: updatedEmails
      };
      setSettings(updatedSettings);
      setNewEmail('');
      
      // Auto-save to database immediately
      try {
        await updateSettings(updatedSettings);
        setSaveStatus('success');
        setTimeout(() => setSaveStatus(null), 2000);
      } catch (error) {
        console.error('Failed to save email:', error);
        setSaveStatus('error');
      }
    }
  };

  const removeEmail = async (email) => {
    const updatedEmails = settings.notification_emails.filter(e => e !== email);
    const updatedSettings = {
      ...settings,
      notification_emails: updatedEmails
    };
    setSettings(updatedSettings);
    
    // Auto-save to database immediately
    try {
      await updateSettings(updatedSettings);
      setSaveStatus('success');
      setTimeout(() => setSaveStatus(null), 2000);
    } catch (error) {
      console.error('Failed to remove email:', error);
      setSaveStatus('error');
    }
  };

  const handlePasswordChange = async () => {
    setPasswordError('');
    setPasswordStatus(null);

    // Validation
    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError('All password fields are required');
      return;
    }

    if (newPassword.length < 8) {
      setPasswordError('New password must be at least 8 characters long');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }

    setIsChangingPassword(true);

    try {
      await changePassword(currentPassword, newPassword);
      setPasswordStatus('success');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setTimeout(() => setPasswordStatus(null), 5000);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to change password';
      setPasswordError(errorMsg);
      setPasswordStatus('error');
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleSendTestReport = async () => {
    setIsSendingTestReport(true);
    setTestReportStatus(null);
    
    try {
      // First, save current settings to ensure recipient email is in database
      await updateSettings(settings);
      
      // Then send the test report
      await sendTestAnalyticsReport();
      setTestReportStatus('success');
      setTimeout(() => setTestReportStatus(null), 5000);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to send test report';
      setTestReportStatus({ type: 'error', message: errorMsg });
    } finally {
      setIsSendingTestReport(false);
    }
  };

  const toggleReportSection = (section) => {
    setSettings(prev => ({
      ...prev,
      analytics_report_sections: {
        ...prev.analytics_report_sections,
        [section]: !prev.analytics_report_sections?.[section]
      }
    }));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div data-testid="settings-panel">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold text-dark-900">Settings</h1>
        
        {saveStatus && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`flex items-center px-4 py-2 rounded-lg ${
              saveStatus === 'success'
                ? 'bg-green-50 text-green-800'
                : 'bg-red-50 text-red-800'
            }`}
          >
            {saveStatus === 'success' ? (
              <>
                <FiCheck className="mr-2" />
                Saved successfully
              </>
            ) : (
              <>
                <FiAlertCircle className="mr-2" />
                Save failed
              </>
            )}
          </motion.div>
        )}
      </div>

      <div className="bg-white rounded-lg shadow-sm p-6 space-y-8">
        {/* Notification Emails */}
        <div>
          <h2 className="text-xl font-bold text-dark-900 mb-4">Notification Recipients</h2>
          <p className="text-gray-600 text-sm mb-4">
            Emails that will receive inquiry notifications
          </p>
          
          <div className="space-y-3 mb-4">
            {settings?.notification_emails?.map((email) => (
              <div key={email} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <span className="text-dark-900">{email}</span>
                <button
                  onClick={() => removeEmail(email)}
                  className="text-red-600 hover:text-red-700"
                >
                  <FiX />
                </button>
              </div>
            ))}
          </div>

          <div className="flex gap-2">
            <input
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="Add new email"
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              onKeyPress={(e) => e.key === 'Enter' && addEmail()}
            />
            <button
              onClick={addEmail}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center"
            >
              <FiPlus className="mr-2" />
              Add
            </button>
          </div>
        </div>

        {/* SMTP / Outgoing Email Settings */}
        <div className="border-t border-gray-200 pt-8">
          <div className="flex items-center gap-2 mb-4">
            <FiMail className="text-xl text-primary-600" />
            <h2 className="text-xl font-bold text-dark-900">Outgoing Email Settings (SMTP)</h2>
          </div>
          <p className="text-gray-600 text-sm mb-6">
            Configure SMTP server credentials for sending emails. Changes are used immediately.
          </p>
          
          <div className="grid md:grid-cols-2 gap-6">
            {/* SMTP Host */}
            <div>
              <label className="block text-sm font-semibold text-dark-900 mb-2">
                <FiServer className="inline mr-1" /> SMTP Host
              </label>
              <input
                type="text"
                value={settings?.smtp_host || ''}
                onChange={(e) => setSettings(prev => ({ ...prev, smtp_host: e.target.value }))}
                placeholder="smtp.zoho.in"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>

            {/* SMTP Port */}
            <div>
              <label className="block text-sm font-semibold text-dark-900 mb-2">
                Port
              </label>
              <input
                type="number"
                value={settings?.smtp_port || ''}
                onChange={(e) => setSettings(prev => ({ ...prev, smtp_port: parseInt(e.target.value) || '' }))}
                placeholder="465"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
              <p className="text-xs text-gray-500 mt-1">Common: 465 (SSL) or 587 (TLS)</p>
            </div>

            {/* SMTP Username */}
            <div>
              <label className="block text-sm font-semibold text-dark-900 mb-2">
                Username / Email
              </label>
              <input
                type="email"
                value={settings?.smtp_username || ''}
                onChange={(e) => setSettings(prev => ({ ...prev, smtp_username: e.target.value }))}
                placeholder="projects@haden.in"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>

            {/* SMTP Password */}
            <div>
              <label className="block text-sm font-semibold text-dark-900 mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  type={showSmtpPassword ? 'text' : 'password'}
                  value={settings?.smtp_password || ''}
                  onChange={(e) => setSettings(prev => ({ ...prev, smtp_password: e.target.value }))}
                  placeholder="Enter SMTP password"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 pr-12"
                />
                <button
                  type="button"
                  onClick={() => setShowSmtpPassword(!showSmtpPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showSmtpPassword ? <FiEyeOff /> : <FiEye />}
                </button>
              </div>
            </div>

            {/* From Email */}
            <div>
              <label className="block text-sm font-semibold text-dark-900 mb-2">
                From Email Address
              </label>
              <input
                type="email"
                value={settings?.smtp_from_email || ''}
                onChange={(e) => setSettings(prev => ({ ...prev, smtp_from_email: e.target.value }))}
                placeholder="projects@haden.in"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
              <p className="text-xs text-gray-500 mt-1">Email address shown to recipients</p>
            </div>

            {/* From Name */}
            <div>
              <label className="block text-sm font-semibold text-dark-900 mb-2">
                From Name
              </label>
              <input
                type="text"
                value={settings?.smtp_from_name || ''}
                onChange={(e) => setSettings(prev => ({ ...prev, smtp_from_name: e.target.value }))}
                placeholder="HADEN Systems"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
              <p className="text-xs text-gray-500 mt-1">Display name shown to recipients</p>
            </div>
          </div>
        </div>

        {/* Digest Settings */}
        <div className="border-t border-gray-200 pt-8">
          <h2 className="text-xl font-bold text-dark-900 mb-4">Digest Emails</h2>
          
          <div className="space-y-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={settings?.digest_enabled || false}
                onChange={(e) => setSettings(prev => ({ ...prev, digest_enabled: e.target.checked }))}
                className="mr-3 h-4 w-4"
              />
              <span className="font-medium">Enable daily/weekly digest emails</span>
            </label>

            {settings?.digest_enabled && (
              <>
                <div>
                  <label className="block text-sm font-semibold text-dark-900 mb-2">
                    Schedule
                  </label>
                  <select
                    value={settings.digest_schedule || 'daily'}
                    onChange={(e) => setSettings(prev => ({ ...prev, digest_schedule: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-dark-900 mb-2">
                    Send Time (IST)
                  </label>
                  <input
                    type="time"
                    value={settings.digest_time || '09:00'}
                    onChange={(e) => setSettings(prev => ({ ...prev, digest_time: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </>
            )}
          </div>
        </div>

        {/* Analytics Report Alerts Section */}
        <div className="border-t border-gray-200 pt-8">
          <div className="flex items-center gap-2 mb-4">
            <FiBarChart2 className="text-xl text-primary-600" />
            <h2 className="text-xl font-bold text-dark-900">Analytics Report Alerts</h2>
          </div>
          <p className="text-gray-600 text-sm mb-6">
            Configure automated analytics reports to be delivered to your inbox.
          </p>
          
          <div className="space-y-6">
            {/* Enable Toggle */}
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={settings?.analytics_report_enabled || false}
                onChange={(e) => setSettings(prev => ({ ...prev, analytics_report_enabled: e.target.checked }))}
                className="mr-3 h-4 w-4"
              />
              <span className="font-medium">Enable scheduled analytics reports</span>
            </label>

            {settings?.analytics_report_enabled && (
              <>
                {/* Recipient Email */}
                <div>
                  <label className="block text-sm font-semibold text-dark-900 mb-2">
                    Report Recipient Email
                  </label>
                  <input
                    type="email"
                    value={settings?.analytics_report_email || ''}
                    onChange={(e) => setSettings(prev => ({ ...prev, analytics_report_email: e.target.value }))}
                    placeholder="your@email.com"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">Analytics report will be sent to this email address</p>
                </div>

                {/* Frequency Selection */}
                <div>
                  <label className="block text-sm font-semibold text-dark-900 mb-2">
                    Report Frequency
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {['daily', 'weekly', 'monthly', 'yearly'].map((freq) => (
                      <button
                        key={freq}
                        type="button"
                        onClick={() => setSettings(prev => ({ ...prev, analytics_report_frequency: freq }))}
                        className={`px-4 py-3 rounded-lg border-2 font-medium transition-all ${
                          settings?.analytics_report_frequency === freq
                            ? 'border-primary-600 bg-primary-50 text-primary-700'
                            : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        {freq.charAt(0).toUpperCase() + freq.slice(1)}
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    {settings?.analytics_report_frequency === 'daily' && 'Reports sent every day'}
                    {settings?.analytics_report_frequency === 'weekly' && 'Reports sent every Monday'}
                    {settings?.analytics_report_frequency === 'monthly' && 'Reports sent on the 1st of each month'}
                    {settings?.analytics_report_frequency === 'yearly' && 'Reports sent on January 1st'}
                    {!settings?.analytics_report_frequency && 'Select how often you want to receive reports'}
                  </p>
                </div>

                {/* Send Time */}
                <div>
                  <label className="block text-sm font-semibold text-dark-900 mb-2">
                    Send Time (IST - Indian Standard Time)
                  </label>
                  <input
                    type="time"
                    value={settings?.analytics_report_time || '09:00'}
                    onChange={(e) => setSettings(prev => ({ ...prev, analytics_report_time: e.target.value }))}
                    className="w-full md:w-48 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">All times are in Indian Standard Time (UTC+5:30)</p>
                </div>

                {/* Report Sections */}
                <div>
                  <label className="block text-sm font-semibold text-dark-900 mb-3">
                    Include in Report
                  </label>
                  <div className="grid md:grid-cols-2 gap-3">
                    {[
                      { key: 'overview', label: 'Performance Overview', desc: 'Visits, inquiries, CTA clicks' },
                      { key: 'geographic', label: 'Geographic Breakdown', desc: 'Visitor countries & cities' },
                      { key: 'devices', label: 'Device & Browser', desc: 'Device types, browsers, OS' },
                      { key: 'traffic_sources', label: 'Traffic Sources', desc: 'Referrer domains' },
                      { key: 'conversion_funnel', label: 'Conversion Funnel', desc: 'Visit-to-inquiry journey' },
                      { key: 'engagement_metrics', label: 'Engagement Metrics', desc: 'Session duration, bounce rate, pages/session' },
                      { key: 'top_performing', label: 'Top Performing Days', desc: 'Best days & hours for traffic' },
                      { key: 'user_journey', label: 'User Journey Flow', desc: 'Visitor-to-inquiry conversion path' },
                      { key: 'heatmap', label: 'Heatmap Analytics', desc: 'Clicks by section, time on page, scroll depth', defaultOff: true },
                      { key: 'realtime', label: 'Real-time Insights', desc: 'Peak hours, session patterns, unique visitors', defaultOff: true },
                    ].map((section) => {
                      // Determine if section is enabled - defaultOff sections are false by default
                      const isEnabled = section.defaultOff 
                        ? settings?.analytics_report_sections?.[section.key] === true
                        : settings?.analytics_report_sections?.[section.key] !== false;
                      
                      return (
                      <label
                        key={section.key}
                        className={`flex items-start p-4 rounded-lg border-2 cursor-pointer transition-all ${
                          isEnabled
                            ? 'border-primary-200 bg-primary-50'
                            : 'border-gray-200 bg-white'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isEnabled}
                          onChange={() => toggleReportSection(section.key)}
                          className="mt-1 h-4 w-4 mr-3"
                        />
                        <div>
                          <span className="font-medium text-dark-900 block">{section.label}</span>
                          <span className="text-xs text-gray-500">{section.desc}</span>
                          {section.defaultOff && (
                            <span className="text-xs text-blue-600 block mt-1">New feature</span>
                          )}
                        </div>
                      </label>
                    )})}
                  </div>
                </div>

                {/* Test Report Button */}
                <div className="pt-4 border-t border-gray-100">
                  {testReportStatus === 'success' && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center text-sm"
                    >
                      <FiCheck className="text-green-600 mr-2" />
                      <span className="text-green-800">Test report sent successfully!</span>
                    </motion.div>
                  )}
                  
                  {testReportStatus?.type === 'error' && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center text-sm"
                    >
                      <FiAlertCircle className="text-red-600 mr-2" />
                      <span className="text-red-800">{testReportStatus.message}</span>
                    </motion.div>
                  )}
                  
                  <button
                    type="button"
                    onClick={handleSendTestReport}
                    disabled={isSendingTestReport || !settings?.analytics_report_email}
                    className="flex items-center px-5 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <FiSend className="mr-2" />
                    {isSendingTestReport ? 'Sending...' : 'Send Test Report'}
                  </button>
                  <p className="text-xs text-gray-500 mt-2">
                    Send a preview of the analytics report to verify your settings
                  </p>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Change Password Section */}
        <div className="border-t border-gray-200 pt-8">
          <div className="flex items-center gap-2 mb-4">
            <FiLock className="text-xl text-dark-900" />
            <h2 className="text-xl font-bold text-dark-900">Change Password</h2>
          </div>
          
          {passwordStatus === 'success' && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center"
            >
              <FiCheck className="text-green-600 mr-2" />
              <span className="text-green-800">Password changed successfully!</span>
            </motion.div>
          )}

          {passwordError && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center"
            >
              <FiAlertCircle className="text-red-600 mr-2" />
              <span className="text-red-800">{passwordError}</span>
            </motion.div>
          )}

          <div className="grid md:grid-cols-2 gap-8">
            {/* Password Form */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-dark-900 mb-2">
                  Current Password
                </label>
                <div className="relative">
                  <input
                    type={showCurrentPassword ? 'text' : 'password'}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 pr-12"
                    placeholder="Enter current password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showCurrentPassword ? <FiEyeOff /> : <FiEye />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-dark-900 mb-2">
                  New Password
                </label>
                <div className="relative">
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 pr-12"
                    placeholder="Enter new password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showNewPassword ? <FiEyeOff /> : <FiEye />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-dark-900 mb-2">
                  Confirm New Password
                </label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 ${
                    confirmPassword && confirmPassword !== newPassword 
                      ? 'border-red-300 bg-red-50' 
                      : confirmPassword && confirmPassword === newPassword 
                        ? 'border-green-300 bg-green-50' 
                        : 'border-gray-300'
                  }`}
                  placeholder="Confirm new password"
                />
                {confirmPassword && confirmPassword !== newPassword && (
                  <p className="text-xs text-red-600 mt-1">Passwords do not match</p>
                )}
                {confirmPassword && confirmPassword === newPassword && (
                  <p className="text-xs text-green-600 mt-1">Passwords match ✓</p>
                )}
              </div>

              <button
                onClick={handlePasswordChange}
                disabled={isChangingPassword || !newPassword || !confirmPassword || newPassword !== confirmPassword}
                className="flex items-center px-6 py-3 bg-dark-900 text-white rounded-lg hover:bg-dark-800 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                <FiLock className="mr-2" />
                {isChangingPassword ? 'Changing Password...' : 'Change Password'}
              </button>
            </div>

            {/* Password Requirements Guide */}
            <div className="bg-gray-50 rounded-lg p-5">
              <h3 className="text-sm font-semibold text-dark-900 mb-4">Password Requirements</h3>
              <div className="space-y-2">
                <PasswordRequirement 
                  met={newPassword.length >= 8} 
                  text="At least 8 characters" 
                />
                <PasswordRequirement 
                  met={/[A-Z]/.test(newPassword)} 
                  text="At least one uppercase letter (A-Z)" 
                />
                <PasswordRequirement 
                  met={/[a-z]/.test(newPassword)} 
                  text="At least one lowercase letter (a-z)" 
                />
                <PasswordRequirement 
                  met={/[0-9]/.test(newPassword)} 
                  text="At least one number (0-9)" 
                />
                <PasswordRequirement 
                  met={/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(newPassword)} 
                  text="At least one special character (!@#$%^&*)" 
                />
                <div className="pt-3 mt-3 border-t border-gray-200">
                  <PasswordRequirement 
                    met={confirmPassword && confirmPassword === newPassword} 
                    text="Passwords match" 
                  />
                </div>
              </div>
              
              {/* Password Strength Meter */}
              {newPassword && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-gray-600">Password Strength</span>
                    <span className={`text-xs font-semibold ${
                      getPasswordStrength(newPassword) === 'Strong' ? 'text-green-600' :
                      getPasswordStrength(newPassword) === 'Medium' ? 'text-yellow-600' : 'text-red-600'
                    }`}>
                      {getPasswordStrength(newPassword)}
                    </span>
                  </div>
                  <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div 
                      className={`h-full transition-all duration-300 ${
                        getPasswordStrength(newPassword) === 'Strong' ? 'w-full bg-green-500' :
                        getPasswordStrength(newPassword) === 'Medium' ? 'w-2/3 bg-yellow-500' : 'w-1/3 bg-red-500'
                      }`}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div className="border-t border-gray-200 pt-6">
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="flex items-center px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:bg-gray-400"
            data-testid="save-settings-btn"
          >
            <FiSave className="mr-2" />
            {isSaving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Password Requirement Component
const PasswordRequirement = ({ met, text }) => (
  <div className="flex items-center">
    {met ? (
      <FiCheck className="text-green-600 mr-2 flex-shrink-0" size={16} />
    ) : (
      <FiX className="text-gray-400 mr-2 flex-shrink-0" size={16} />
    )}
    <span className={`text-sm ${met ? 'text-green-700' : 'text-gray-600'}`}>{text}</span>
  </div>
);

// Password Strength Calculator
const getPasswordStrength = (password) => {
  let score = 0;
  if (password.length >= 8) score++;
  if (password.length >= 12) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/[a-z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) score++;
  
  if (score >= 5) return 'Strong';
  if (score >= 3) return 'Medium';
  return 'Weak';
};

export default SettingsPanel;